import { useEffect, useRef, useState } from "react";
import { useCustomerBasketAdapter } from "@shared-core/data/CustomerBasketAdapterContext";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import type {
  CustomerBasketLine,
  CustomerLineDeliveryPreview,
} from "@shared-core/data/customer-basket-types";
import {
  customerBasketFailure,
  type CustomerBasketFailure,
} from "@shared-core/domain/customer-basket-presentation";
import { startingAddressId } from "@shared-core/domain/customer-address-book";
import { formatPriceOrNotYetPriced } from "@shared-core/domain/checkout-delivery";
import { deliveryDateOptionsOf } from "@shared-core/domain/delivery-dates";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import {
  changeOf,
  rowsOfLine,
  rowsProblem,
  rowsTotal,
  type DeliveryRow,
} from "@shared-core/domain/line-delivery-change";
import { useAddressPicker } from "@shared-core/domain/useAddressPicker";
import { Button } from "../../../app/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../app/components/primitives/dialog";
import { Input } from "../../../app/components/primitives/input";
import { Label } from "../../../app/components/primitives/label";
import { DeliveryAddressPicker } from "../catalogue-item/DeliveryAddressPicker";
import { DeliveryDateChoice } from "../catalogue-item/DeliveryDateChoice";
import { usePortalViewer } from "../PortalViewerContext";
import { Loader2, X } from "lucide-react";

// Change delivery (docs/features/customer-portal-ordering/22 section 7): the date, the address, or the split across
// addresses of a line already in the basket, changed in place. It reuses the item page's address picker (with "Send to
// another address" rows for a split) and date choice. The dialog asks the server what each date would cost for the
// delivery as it now stands (a preview that stores nothing), shows the line's new price against its old one, and saves
// at the price it showed: a price that has moved since is refused with the new price, which the person is shown and
// confirms (nothing is applied until they do). A date that stops being offered is never swapped for another one
// silently: the person is told and chooses again. A line of several versions changes its date only. The dates, prices
// and refusals are the server's.
export function BasketLineDeliveryDialog({
  line,
  open,
  onOpenChange,
  onChanged,
}: {
  line: CustomerBasketLine;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The change was saved: the basket is read again. */
  onChanged: () => void;
}) {
  const previewUrl = line.actions.preview_delivery_url ?? null;
  const changeUrl = line.actions.change_delivery_url ?? null;
  if (previewUrl === null || changeUrl === null) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <ChangeDeliveryContent
          line={line}
          previewUrl={previewUrl}
          changeUrl={changeUrl}
          close={() => onOpenChange(false)}
          onChanged={onChanged}
        />
      </DialogContent>
    </Dialog>
  );
}

type PreviewState =
  | { status: "loading"; last: CustomerLineDeliveryPreview | null }
  | { status: "ready"; preview: CustomerLineDeliveryPreview }
  | { status: "error"; message: string };

/** The sentence for a date the person chose that the delivery no longer offers: they choose again. */
const DATE_GONE = "That date isn't offered for this delivery. Choose another.";

function ChangeDeliveryContent({
  line,
  previewUrl,
  changeUrl,
  close,
  onChanged,
}: {
  line: CustomerBasketLine;
  previewUrl: string;
  changeUrl: string;
  close: () => void;
  onChanged: () => void;
}) {
  const baskets = useCustomerBasketAdapter();
  const data = useCustomerDataAdapter();
  const { viewKey, sessionState } = usePortalViewer();
  const customerCode =
    sessionState.status === "success"
      ? (sessionState.data.ordering_customer_code ?? undefined)
      : undefined;
  // A line of several versions keeps its addresses and quantities (the item page splits them): its date alone changes.
  const isDateOnly = (line.versions ?? []).length > 0;

  const { state: pickerState, reload: reloadAddresses } = useAddressPicker(
    baskets,
    data,
    customerCode,
    viewKey,
  );
  const picker = pickerState.status === "ready" ? pickerState.picker : null;
  const addresses = picker?.addresses ?? [];

  // Where the line goes, as rows: one for one address, two or more for a split. It starts as the line's own.
  const [rows, setRows] = useState<DeliveryRow[]>(() => rowsOfLine(line));
  // An address the line goes to that the picker does not hold (taken out of the person's book) starts on one it does.
  const hasReconciled = useRef(false);
  useEffect(() => {
    if (picker === null || hasReconciled.current) return;
    hasReconciled.current = true;
    const known = new Set(picker.addresses.map((a) => a.address_id));
    setRows((current) => {
      const start = startingAddressId(picker.addresses);
      if (current.length === 0)
        return start === null
          ? current
          : [{ addressId: start, quantity: line.quantity }];
      const used = new Set(current.map((r) => r.addressId));
      return current.map((row) => {
        if (known.has(row.addressId)) return row;
        const free = picker.addresses.find((a) => !used.has(a.address_id));
        const replacement = free?.address_id ?? start;
        if (replacement === null || replacement === undefined) return row;
        used.add(replacement);
        return { ...row, addressId: replacement };
      });
    });
  }, [picker, line.quantity]);

  const [chosenCode, setChosenCode] = useState<string | null>(null);
  const chosenRef = useRef<string | null>(null);
  // After a date is refused or dropped by a new address the person chooses again; the item's default is not applied.
  const [mustChoose, setMustChoose] = useState(false);
  const mustChooseRef = useRef(false);
  const [dateNote, setDateNote] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState>({
    status: "loading",
    last: null,
  });
  const [reloadKey, setReloadKey] = useState(0);
  const requestSeq = useRef(0);
  const [isSaving, setIsSaving] = useState(false);
  const [failure, setFailure] = useState<CustomerBasketFailure | null>(null);
  // Set once a save is refused because the price moved: the new price, shown and confirmed before it is applied.
  const [movedPence, setMovedPence] = useState<number | null>(null);

  const problem = isDateOnly ? null : rowsProblem(rows);
  const canAsk = rows.length > 0 || isDateOnly;
  const destinationKey = JSON.stringify([isDateOnly ? [] : rows, reloadKey]);

  function pickDate(code: string | null, isForced = false) {
    chosenRef.current = code;
    setChosenCode(code);
    mustChooseRef.current = isForced;
    setMustChoose(isForced);
  }

  useEffect(() => {
    if (!canAsk || problem !== null) return;
    const seq = ++requestSeq.current;
    setPreview((current) => ({
      status: "loading",
      last: current.status === "ready" ? current.preview : null,
    }));
    setMovedPence(null);
    // The date this ask was made at: a date the person picks while it is out is theirs, and the answer leaves it be.
    const askedAt = chosenRef.current;
    baskets
      .previewBasketLineDelivery(
        previewUrl,
        changeOf({
          rows,
          speedCode: askedAt,
          isDateOnly,
          lineQuantity: line.quantity,
        }),
      )
      .then((result) => {
        if (seq !== requestSeq.current) return;
        setPreview({ status: "ready", preview: result });
        if (chosenRef.current !== askedAt) return;
        if (askedAt !== null && result.selected_code !== askedAt) {
          // The date they chose is not offered here: say so, and let them choose again.
          pickDate(null, true);
          setDateNote(DATE_GONE);
        } else if (mustChooseRef.current) {
          pickDate(null, true);
        } else {
          pickDate(result.selected_code, false);
          setDateNote(null);
        }
      })
      .catch((err: unknown) => {
        if (seq !== requestSeq.current) return;
        setPreview({
          status: "error",
          message: customerBasketFailure(err).summary,
        });
      });
    // The destination (and a reload) is what a new preview is for; the date chosen is read from the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baskets, previewUrl, destinationKey, canAsk, problem]);

  const shown =
    preview.status === "ready"
      ? preview.preview
      : preview.status === "loading"
        ? preview.last
        : null;
  const isAsking = preview.status === "loading";
  const options = shown ? deliveryDateOptionsOf(shown.speeds, chosenCode) : [];
  const chosen = options.find((o) => o.isSelected) ?? null;
  const newGoodsPence = movedPence ?? chosen?.pricePence ?? null;
  const canSave =
    preview.status === "ready" &&
    chosen !== null &&
    !chosen.isToBeConfirmed &&
    problem === null &&
    !isSaving;

  async function save() {
    if (chosen === null || newGoodsPence === null) return;
    setIsSaving(true);
    setFailure(null);
    try {
      await baskets.changeBasketLineDelivery(changeUrl, {
        ...changeOf({
          rows,
          speedCode: chosen.speedCode,
          isDateOnly,
          lineQuantity: line.quantity,
        }),
        expected_price_pence: newGoodsPence,
      });
      onChanged();
      close();
    } catch (err) {
      const refused = customerBasketFailure(err);
      if (refused.movedPrice) {
        // Nothing was changed: the new price is shown and applied only when the person confirms it.
        setMovedPence(refused.movedPrice.newPricePence);
      } else if (refused.fieldErrors.speed_code) {
        // The date has gone: ask again for the dates on offer and let the person choose, never a silent default.
        setDateNote(refused.fieldErrors.speed_code);
        pickDate(null, true);
        setReloadKey((k) => k + 1);
      } else {
        setFailure(refused);
      }
    } finally {
      setIsSaving(false);
    }
  }

  function updateRow(index: number, patch: Partial<DeliveryRow>) {
    setRows((current) =>
      current.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  }

  function sendToAnotherAddress() {
    setRows((current) => {
      const used = new Set(current.map((row) => row.addressId));
      const next =
        addresses.find((a) => !used.has(a.address_id))?.address_id ??
        addresses[0]?.address_id ??
        null;
      return next === null
        ? current
        : [...current, { addressId: next, quantity: 0 }];
    });
  }

  function removeRow(index: number) {
    setRows((current) => current.filter((_, i) => i !== index));
  }

  // An address taken out of the book: a row on it moves to where the picker starts without it.
  function onAddressRemoved(removedId: string) {
    const left = addresses.filter((a) => a.address_id !== removedId);
    const start = startingAddressId(left);
    if (start === null) return;
    setRows((current) =>
      current.map((row) =>
        row.addressId === removedId ? { ...row, addressId: start } : row,
      ),
    );
  }

  const fieldMessage =
    failure?.fieldErrors.delivery_address_id ??
    failure?.fieldErrors.delivery_lines ??
    failure?.fieldErrors.quantity ??
    null;
  const isSplit = rows.length > 1;
  const total = rowsTotal(rows);

  return (
    <>
      <DialogHeader>
        <DialogTitle>Change delivery</DialogTitle>
        <DialogDescription>
          {line.title}
          {isDateOnly
            ? ". This item has several versions, so you can change its delivery date here. To change where it goes, add it again from its page."
            : ""}
        </DialogDescription>
      </DialogHeader>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto">
        {!isDateOnly && (
          <section className="space-y-3" aria-label="Where it goes">
            {pickerState.status === "loading" && (
              <p className="text-sm text-muted-foreground">
                Loading your addresses…
              </p>
            )}
            {pickerState.status === "error" && (
              <div role="alert" className="space-y-2 text-sm">
                <p className="text-red-700 dark:text-red-400">
                  We couldn&apos;t load your delivery addresses.
                </p>
                <Button size="sm" variant="outline" onClick={reloadAddresses}>
                  Try again
                </Button>
              </div>
            )}
            {picker && isSplit && (
              <div className="space-y-2">
                {rows.map((row, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-[minmax(0,1fr)_7rem_auto] items-start gap-2"
                  >
                    <DeliveryAddressPicker
                      picker={picker}
                      chosenId={row.addressId}
                      onChoose={(id) => updateRow(index, { addressId: id })}
                      onRemoved={onAddressRemoved}
                      reload={reloadAddresses}
                      canManage={false}
                      disabled={isSaving}
                      changeLabel={`Change delivery address ${index + 1}`}
                    />
                    <div className="space-y-1.5">
                      <Label htmlFor={`change-delivery-qty-${index}`}>
                        Qty
                      </Label>
                      <Input
                        id={`change-delivery-qty-${index}`}
                        inputMode="numeric"
                        className="text-right tabular-nums"
                        value={row.quantity ? row.quantity.toString() : ""}
                        disabled={isSaving}
                        onChange={(event) =>
                          updateRow(index, {
                            quantity:
                              Number.parseInt(
                                event.target.value.replace(/\D/g, ""),
                                10,
                              ) || 0,
                          })
                        }
                      />
                    </div>
                    <Button
                      className="mt-5"
                      variant="ghost"
                      size="icon"
                      aria-label="Remove address"
                      disabled={isSaving}
                      onClick={() => removeRow(index)}
                    >
                      <X />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            {picker && !isSplit && rows[0] && (
              <DeliveryAddressPicker
                picker={picker}
                chosenId={rows[0].addressId}
                onChoose={(id) => updateRow(0, { addressId: id })}
                onRemoved={onAddressRemoved}
                reload={reloadAddresses}
                canManage
                disabled={isSaving}
                isInvalid={Boolean(failure?.fieldErrors.delivery_address_id)}
              />
            )}
            {picker && addresses.length > 1 && rows.length > 0 && (
              <div className="flex items-center justify-between gap-3">
                <Button
                  variant="link"
                  className="h-auto px-0"
                  disabled={isSaving}
                  onClick={sendToAnotherAddress}
                >
                  Send to another address
                </Button>
                {isSplit && (
                  <p
                    className="text-sm text-muted-foreground tabular-nums"
                    data-testid="change-delivery-total"
                  >
                    {`${total.toLocaleString("en-GB")} in all`}
                  </p>
                )}
              </div>
            )}
            {problem !== null && (
              <p role="note" className="text-sm text-red-700 dark:text-red-400">
                {problem}
              </p>
            )}
          </section>
        )}

        <section
          className="space-y-3"
          aria-label="When it arrives"
          aria-busy={isAsking}
        >
          {preview.status === "error" && (
            <div role="alert" className="space-y-2 text-sm">
              <p className="text-red-700 dark:text-red-400">
                {preview.message}
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setReloadKey((k) => k + 1)}
              >
                Try again
              </Button>
            </div>
          )}
          {shown === null &&
            preview.status === "loading" &&
            problem === null && (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Checking the dates and prices…
              </p>
            )}
          {shown !== null && (
            <DeliveryDateChoice
              options={options}
              onSelect={(code) => {
                pickDate(code, false);
                setDateNote(null);
                setMovedPence(null);
                setFailure(null);
              }}
              disabled={isSaving}
            />
          )}
          {mustChoose && dateNote && (
            <p
              role="alert"
              className="text-sm text-amber-800 dark:text-amber-300"
            >
              {dateNote}
            </p>
          )}
        </section>

        {shown !== null && (
          <dl
            className="space-y-0.5 text-sm tabular-nums"
            aria-label="What it costs"
          >
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Price (ex VAT)</dt>
              <dd className="font-medium" data-testid="change-delivery-price">
                {newGoodsPence !== null && newGoodsPence !== line.goods_pence
                  ? `was ${formatQuotedPrice(line.goods_pence)}, now ${formatQuotedPrice(newGoodsPence)}`
                  : formatQuotedPrice(line.goods_pence)}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Delivery (ex VAT)</dt>
              <dd>{formatPriceOrNotYetPriced(shown.delivery_price_pence)}</dd>
            </div>
            {shown.quantity !== line.quantity && (
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Quantity</dt>
                <dd>
                  {`was ${line.quantity.toLocaleString("en-GB")}, now ${shown.quantity.toLocaleString("en-GB")}`}
                </dd>
              </div>
            )}
          </dl>
        )}

        {movedPence !== null && (
          <div
            role="alert"
            className="space-y-1 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200"
          >
            <p>
              The price has changed to {formatQuotedPrice(movedPence)} ex VAT.
              Check it, then confirm your change.
            </p>
          </div>
        )}
        {failure && (
          <p role="alert" className="text-sm text-red-700 dark:text-red-400">
            {fieldMessage ?? failure.summary}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button variant="ghost" disabled={isSaving} onClick={close}>
          Cancel
        </Button>
        <Button disabled={!canSave} onClick={() => void save()}>
          {isSaving && <Loader2 className="animate-spin" />}
          {movedPence !== null && newGoodsPence !== null
            ? `Save at ${formatQuotedPrice(newGoodsPence)} ex VAT`
            : "Save change"}
        </Button>
      </DialogFooter>
    </>
  );
}
